import React, { useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Check, Copy, LocateFixed } from "lucide-react";
import { ReviewKind } from "../utils/copyToVersion";
import { locateField } from "./PdfForm";

const GROUPS = [
    {
        kind: ReviewKind.Missing,
        title: "Not copied: not in the new version",
        hint: "These fields no longer exist. The values stay only in this form.",
    },
    {
        kind: ReviewKind.Invalid,
        title: "Not copied: invalid in the new version",
        hint: "The field exists, but this value is not valid there. It will be empty in the new form.",
    },
    {
        kind: ReviewKind.TooLong,
        title: "Copied, but too long",
        hint: "Copied in full, but the field of the new version shows only part of it.",
    },
];

const ReviewItem = ({ item }) => {
    const [copied, setCopied] = useState(false);
    const value = String(item.value);

    const onCopyValue = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (error) {}
    };

    return (
        <li className={clsx("crl-item", `crl-item--${item.kind}`)}>
            <div className="crl-item__body">
                <div className="crl-item__location">
                    {item.location.page && <span className="crl-item__page">Page {item.location.page}</span>}
                    <span className="crl-item__label">{item.location.label}</span>
                </div>
                <div className="crl-item__value">{value}</div>
                <div className="crl-item__reason">{item.reason}</div>
            </div>
            <div className="crl-item__actions">
                <button type="button" className="crl-icon-btn" onClick={onCopyValue} title="Copy the value">
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
                <button type="button" className="crl-icon-btn" onClick={() => locateField(item.pdfField)} title="Locate in the PDF">
                    <LocateFixed size={16} />
                </button>
            </div>
        </li>
    );
};

/** Values of the old version that are not copied as they are, grouped by why. */
const CopyReviewList = ({ items }) => (
    <section className="copy-review-list" aria-label="Values not copied as they are">
        <div className="crl-legend">
            <span className="crl-legend__item crl-legend__item--copied">Copied</span>
            <span className="crl-legend__item crl-legend__item--warning">Copied, too long</span>
            <span className="crl-legend__item crl-legend__item--notCopied">Not copied</span>
        </div>
        <div className="crl-list">
            {!items.length && <p className="crl-empty">Every value is copied to the new version as it is.</p>}
            {GROUPS.map((group) => {
                const groupItems = items.filter((item) => item.kind === group.kind);
                if (!groupItems.length) return null;
                return (
                    <div key={group.kind} className={clsx("crl-group", `crl-group--${group.kind}`)}>
                        <h4 className="crl-group__title">
                            {group.title} <span>({groupItems.length})</span>
                        </h4>
                        <p className="crl-group__hint">{group.hint}</p>
                        <ul>
                            {groupItems.map((item) => (
                                <ReviewItem key={item.pdfField} item={item} />
                            ))}
                        </ul>
                    </div>
                );
            })}
        </div>
    </section>
);

const reviewItemShape = PropTypes.shape({
    pdfField: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    kind: PropTypes.oneOf(Object.values(ReviewKind)).isRequired,
    reason: PropTypes.string,
    copied: PropTypes.bool,
    location: PropTypes.shape({ page: PropTypes.number, label: PropTypes.string }).isRequired,
});

ReviewItem.propTypes = {
    item: reviewItemShape.isRequired,
};

CopyReviewList.propTypes = {
    items: PropTypes.arrayOf(reviewItemShape).isRequired,
};

export default CopyReviewList;
